import React from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  Text,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Link as ChakraLink,
  Badge,
  Select,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { createShortLink, listShortLinks, getShortLinkStats } from '../../services/shortlinks';
import { FiClipboard, FiExternalLink, FiRefreshCcw, FiBarChart2 } from 'react-icons/fi';
import { getArticles } from '../../services/articles';
import { getEvents } from '../../services/eventService';

const ShortlinksAdminPage: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === 'admin';
  const qc = useQueryClient();
  const [selectedTarget, setSelectedTarget] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [code, setCode] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const statsModal = useDisclosure();
  const [statsLink, setStatsLink] = React.useState<any>(null);
  const [statsData, setStatsData] = React.useState<any>(null);

  const linksQ = useQuery({
    queryKey: ['admin-shortlinks'],
    queryFn: listShortLinks,
    staleTime: 60_000,
  });

  // Fetch latest published articles (limit ~50) and activities for dropdown
  const articlesQ = useQuery({
    queryKey: ['shortlinks-articles'],
    queryFn: () => getArticles({ published: true, page_size: 50 }),
    staleTime: 60_000,
  });
  const eventsQ = useQuery({
    queryKey: ['shortlinks-events'],
    queryFn: () => getEvents(),
    staleTime: 60_000,
  });

  const staticTargets: Array<{ label: string; path: string }> = React.useMemo(() => ([
    { label: 'Domů', path: '/' },
    { label: 'Blog', path: '/blog' },
    { label: 'Kalendář', path: '/kalendar' },
    { label: 'Aktivity', path: '/aktivity' },
    { label: 'Tabulky', path: '/tabulky' },
    { label: 'Zápasy', path: '/zapasy' },
    { label: 'Hráči', path: '/hraci' },
    { label: 'Sponzoři', path: '/sponzori' },
    { label: 'Galerie', path: '/galerie' },
    { label: 'Videa', path: '/videa' },
    { label: 'Kontakt', path: '/kontakt' },
    { label: 'O klubu', path: '/o-klubu' },
    { label: 'Oblečení', path: '/obleceni' },
  ]), []);

  const handleCreate = async () => {
    const val = selectedTarget.trim();
    if (!val) { toast({ title: 'Vyberte cíl odkazu', status: 'warning' }); return; }
    try {
      setCreating(true);
      // sanitize code early for UX
      const rawCode = code.trim();
      const safeCode = rawCode ? rawCode.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 16) : undefined;
      // Decode selection to absolute URL + optional source metadata
      let target_url = '';
      let source_type: string | undefined;
      let source_id: number | undefined;
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (val.startsWith('static:')) {
        const path = val.substring('static:'.length);
        target_url = origin + path;
        source_type = 'page';
      } else if (val.startsWith('article:')) {
        const rest = val.substring('article:'.length); // id[:slug]
        const [idStr, slug] = rest.split(':');
        const idNum = Number(idStr);
        source_type = 'article';
        if (!isNaN(idNum)) source_id = idNum;
        // Always use the ID-based URL for reliability, slug-based URLs can break if slug changes
        target_url = origin + `/articles/${idStr}`;
      } else if (val.startsWith('event:')) {
        const idStr = val.substring('event:'.length);
        const idNum = Number(idStr);
        source_type = 'event';
        if (!isNaN(idNum)) source_id = idNum;
        target_url = origin + `/aktivita/${idStr}`;
      }
      if (!target_url) { toast({ title: 'Neplatná volba cíle', status: 'error' }); return; }
      const defaultCode = !safeCode && source_id && source_type
        ? (source_type === 'article' ? `a${source_id}` : (source_type === 'event' ? `e${source_id}` : undefined))
        : undefined;
      const res = await createShortLink({ target_url, title: title.trim() || undefined, code: (safeCode || defaultCode), active: true, ...(source_type ? { source_type } : {}), ...(source_id ? { source_id } : {}) });
      await navigator.clipboard.writeText(res.short_url);
      toast({ title: 'Odkaz vytvořen', description: `Zkopírováno: ${res.short_url}`, status: 'success' });
      setSelectedTarget(''); setTitle(''); setCode('');
      qc.invalidateQueries({ queryKey: ['admin-shortlinks'] });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.response?.data?.details || e?.message || 'Zkuste to znovu';
      toast({ title: 'Vytvoření selhalo', description: String(msg), status: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const openStats = async (item: any) => {
    try {
      setStatsLink(item);
      setStatsData(null);
      statsModal.onOpen();
      const data = await getShortLinkStats(item.id);
      setStatsData(data);
    } catch (e: any) {
      toast({ title: 'Načtení statistik selhalo', status: 'error' });
    }
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold">Zkrácené odkazy</Text>
          <IconButton aria-label="Obnovit" icon={<FiRefreshCcw />} onClick={() => qc.invalidateQueries({ queryKey: ['admin-shortlinks'] })} />
        </HStack>

        {/* Create form */}
        <Box borderWidth="1px" borderRadius="lg" p={4} mb={6} bg="bg.card">
          <Text fontWeight="semibold" mb={2}>Vytvořit nový odkaz</Text>
          <HStack spacing={2} flexWrap="wrap">
            <Select placeholder="Vyberte cíl" value={selectedTarget} onChange={(e)=>setSelectedTarget(e.target.value)} flex={3}>
              <option value="" disabled>— Vnitřní stránky —</option>
              {staticTargets.map((opt) => (
                <option key={opt.path} value={`static:${opt.path}`}>{opt.label}</option>
              ))}
              <option value="" disabled>— Články (publikované) —</option>
              {(articlesQ.data?.data || []).map((a: any) => (
                <option key={a.id} value={`article:${a.id}:${a.slug || ''}`.replace(/:$/, '')}>{a.title}</option>
              ))}
              <option value="" disabled>— Aktivity —</option>
              {(eventsQ.data || []).filter((ev: any) => ev?.is_public !== false).slice(0, 50).map((ev: any) => (
                <option key={ev.id} value={`event:${ev.id}`}>{ev.title}</option>
              ))}
            </Select>
            <Input placeholder="Titulek (volitelný)" value={title} onChange={(e)=>setTitle(e.target.value)} flex={2} />
            <Input placeholder="Vlastní kód (volitelné)" value={code} onChange={(e)=>setCode(e.target.value)} flex={1} maxLength={16} pattern="[A-Za-z0-9_-]+" title="Povoleno: písmena, čísla, -, _ (max 16 znaků)" />
            <Button onClick={handleCreate} isLoading={creating} colorScheme="blue">Vytvořit</Button>
          </HStack>
        </Box>

        {/* List */}
        <Box borderWidth="1px" borderRadius="lg" overflowX="auto" bg="bg.card">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Kód</Th>
                <Th>Cíl</Th>
                <Th>Titulek</Th>
                <Th>Zdroj</Th>
                <Th>Prokliky</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {linksQ.data?.items?.map((it: any) => {
                const shortUrl = `${window.location.origin}/s/${it.code}`;
                const source = it.source_type ? `${it.source_type}${it.source_id ? `#${it.source_id}` : ''}` : '-';
                return (
                  <Tr key={it.id}>
                    <Td><Badge colorScheme="blue">{it.code}</Badge></Td>
                    <Td maxW="420px">
                      <ChakraLink href={it.target_url} isExternal color="blue.600">{it.target_url}</ChakraLink>
                    </Td>
                    <Td>{it.title || '-'}</Td>
                    <Td>{source}</Td>
                    <Td>{it.click_count ?? 0}</Td>
                    <Td>
                      <HStack>
                        <IconButton aria-label="Otevřít krátkou URL" icon={<FiExternalLink />} as={ChakraLink as any} href={shortUrl} isExternal />
                        <IconButton aria-label="Zkopírovat" icon={<FiClipboard />} onClick={async ()=>{ await navigator.clipboard.writeText(shortUrl); toast({ title: 'Zkopírováno', description: shortUrl, status: 'success', duration: 2000 }); }} />
                        {isAdmin && (
                          <IconButton aria-label="Statistiky" icon={<FiBarChart2 />} onClick={()=> openStats(it)} />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
              {(!linksQ.data?.items || linksQ.data.items.length === 0) && (
                <Tr><Td colSpan={6}><Text p={3}>Žádné odkazy</Text></Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Stats modal (admins only) */}
        {isAdmin && (
        <Modal isOpen={statsModal.isOpen} onClose={statsModal.onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Statistiky: {statsLink?.code}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {!statsData ? (
                <Text>Načítání…</Text>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Prokliky za posledních 30 dní</Text>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Den</Th><Th isNumeric>Počet</Th></Tr></Thead>
                      <Tbody>
                        {statsData.timeseries?.map((row: any, idx: number) => (
                          <Tr key={idx}><Td>{row.date}</Td><Td isNumeric>{row.count}</Td></Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Referrers (Top)</Text>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Referrer</Th><Th isNumeric>Počet</Th></Tr></Thead>
                      <Tbody>
                        {(statsData.referrers || []).map((r: any, i: number) => (
                          <Tr key={i}><Td>{r.Referrer || '-'}</Td><Td isNumeric>{r.Count}</Td></Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>UTM kombinace (Top)</Text>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Source</Th><Th>Medium</Th><Th>Campaign</Th><Th isNumeric>Počet</Th></Tr></Thead>
                      <Tbody>
                        {(statsData.utms || []).map((r: any, i: number) => (
                          <Tr key={i}><Td>{r.Source || '-'}</Td><Td>{r.Medium || '-'}</Td><Td>{r.Campaign || '-'}</Td><Td isNumeric>{r.Count}</Td></Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={statsModal.onClose}>Zavřít</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        )}
      </Box>
    </AdminLayout>
  );
};

export default ShortlinksAdminPage;
